// src/web/client/src/components/subscription/SubscriptionAlert.jsx
import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Crown, Zap } from "lucide-react";
import { API_BASE_URL } from '../../constants';
import { storage } from '../../utils/storage';
import { authenticatedFetch } from "@/utils/authenticatedFetch";
export default function SubscriptionAlert() {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // השהיה קטנה כדי לוודא שה-localStorage אותחל
    const timer = setTimeout(() => {
      fetchSubscriptionInfo();
    }, 500); // חצי שנייה השהייה

    return () => clearTimeout(timer);
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      // קבל shop domain בצורה בטוחה
      const shopDomain = storage.getShopDomain();
      
      console.log('SubscriptionAlert - Fetching for shop:', shopDomain);
      
      // וודא שיש ערך תקין
      if (!shopDomain || shopDomain === 'undefined') {
        console.error('Invalid shop domain, skipping subscription fetch');
        setIsLoading(false);
        return;
      }
 const response = await authenticatedFetch(`${API_BASE_URL}/subscription/info?shop_domain=${shopDomain}`);

      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      } else if (response.status === 404) {
        console.log('Subscription info not found, likely no subscription');
      } else {
        console.error('Failed to fetch subscription info:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    const shopDomain = storage.getShopDomain();
    window.location.href = `https://ecomoria.com/billing?shop=${shopDomain}`;
  };

  // אל תציג כלום בזמן טעינה או אם אין מידע
  if (isLoading || !subscriptionInfo) {
    return null;
  }

  // התראה לתקופת ניסיון מסתיימת
  if (subscriptionInfo.status === 'trial' && subscriptionInfo.trial_days_left <= 3) {
    return (
      <Alert className="border-orange-200 bg-orange-50 mb-6">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="flex items-center justify-between">
          <div>
          <strong>Your trial period is ending soon!</strong>
<p className="text-sm mt-1">
  {subscriptionInfo.trial_days_left} days left in your trial period.<br />
  Upgrade now to keep enjoying all features.
</p>

          </div>
          <Button onClick={handleUpgrade} className="ml-4">
            <Crown className="w-4 h-4 mr-2" />
Upgrade Now
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Monthly-message-plan alert. Pro shops (messages_limit === -1) never
  // hit this branch because the outer guard requires messages_limit > 0.
  //
  // WARN-AND-CONTINUE: passing the plan no longer stops sending, so the
  // over-limit copy must say "still sending", never "reached your limit /
  // wait for the reset". This banner renders on the Dashboard ONLY —
  // merchants living in other screens rely on the quota alert emails.
  //
  // Three visible states share a single <Alert> render — the styling
  // (yellow → red) and copy (approaching → over plan) switch on the
  // overPlan / >=90 thresholds. Behaviour thresholds are unchanged from
  // pre-English-copy:
  //   80–89% → yellow banner, no button (soft warning)
  //   90–99% → yellow banner + "Upgrade plan" button
  //   100+%  → red banner + "Upgrade to Pro — $49.99/mo" (concrete CTA
  //            with the plan+price so the merchant doesn't have to click
  //            through to discover them). Pro pricing is the source of
  //            truth from backend BILLING_PLANS.pro.price ($49.99);
  //            "unlimited messages" reflects messages_per_month: -1.
  if (subscriptionInfo.messages_limit > 0) {
    const usagePercent = (subscriptionInfo.messages_used / subscriptionInfo.messages_limit) * 100;
    const overPlan = usagePercent >= 100;

    if (usagePercent >= 80) {
      return (
        <Alert className={`mb-6 ${overPlan ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
          <Zap className={`h-4 w-4 ${overPlan ? 'text-red-600' : 'text-yellow-600'}`} />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <strong>
                {overPlan
                  ? "You're over your monthly plan — emails are still sending"
                  : "You're approaching your message limit"}
              </strong>
              <p className="text-sm mt-1">
                {subscriptionInfo.messages_used} of {subscriptionInfo.messages_limit} plan messages used
                {overPlan
                  ? '. Nothing has stopped — every email keeps going out. Upgrade to Pro for unlimited messages.'
                  : ' this month.'}
              </p>
            </div>
            {usagePercent >= 90 && (
              <Button onClick={handleUpgrade} className="ml-4">
                {overPlan ? 'Upgrade to Pro — $49.99/mo' : 'Upgrade plan'}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      );
    }
  }

  return null;
}